import React, { useState } from "react";
import { MessageTypeEnum } from "domain/enum/messageType";
import { Vacation } from "domain/entities/vacations";
import { VacationStatusEnum } from "domain/enum/vacationStatus";
import {
  useUpdateVacationPlanMutation,
  useVacationPlanById,
} from "presentation/hooks/use-query-vacation";
import Spinner from "presentation/components/Shared/Spinner";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";

export interface VacationSettingParams {
  managerId?: string;
  date?: Date;
  isAdmin?: boolean;
  isManager?: boolean;
  projectId?: string;
  vacationId?: string;
  task?: string;
  onClose?: () => void;
  showMessage: (type: MessageTypeEnum, subject: string, message: string) => void;
}

export const MobileVacationSetting: React.FC<VacationSettingParams> = ({
  vacationId,
  task = "Pedido",
  onClose,
  showMessage,
}) => {
  const [visibleRejectionReason, setVisibleRejectionReason] = useState(false);
  const [reasonReject, setReasonReject] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isCancellation = task === "Cancelamento";

  // Obter os detalhes da solicitação de férias pelo ID
  const { data: vacationData, isLoading, isRefetching } = useVacationPlanById(vacationId);
  const mutation = useUpdateVacationPlanMutation();

  const vacation = vacationData?.data;

  const formatDate = (date?: Date | string | null) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleApproveOrReject = async (status: string) => {
    if (!vacation?.id) return;

    if (
      ((task === "Pedido" && status === VacationStatusEnum.NAO_APROVADO) ||
        (task === "Cancelamento" && status === VacationStatusEnum.APROVADO)) &&
      reasonReject.trim() === ""
    ) {
      return;
    }

    setVisibleRejectionReason(false);
    setIsSubmitting(true);

    const rejectionReasonToSend =
      (task === "Pedido" && status === VacationStatusEnum.NAO_APROVADO) ||
        (task === "Cancelamento" && status === VacationStatusEnum.APROVADO)
        ? reasonReject
        : undefined;

    try {
      await mutation.mutateAsync({
        id: vacation.id,
        status: status,
        rejectionReason: rejectionReasonToSend,
      } as Vacation);

      setReasonReject("");
      const getSuccessDetail = () => {
        if (isCancellation) {
          return status === "Cancelado" ? "Cancelamento de férias aprovado com sucesso."
            : "Pedido de cancelamento rejeitado com sucesso.";
        }
        return status === VacationStatusEnum.NAO_APROVADO ? "Solicitação de férias rejeitada com sucesso."
          : "Solicitação de férias aprovada com sucesso.";
      };
      showMessage(MessageTypeEnum.SUCCESS, "Sucesso", getSuccessDetail());

      setTimeout(() => {
        if (onClose) onClose();
      }, 1500);

    } catch (error: any) {
      const defaultMessage = "Erro ao atualizar solicitação de férias.";
      let errors: string[] = [];

      try {
        errors = JSON.parse(error.message);
      } catch {
        errors = [defaultMessage];
      }

      showMessage(MessageTypeEnum.ERROR, "Erro", errors.join("\n"));


    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || isRefetching) {
    return (
      <div className="flex justify-content-center align-items-center p-5">
        <Spinner />
      </div>
    );
  }

  if (!vacation) {
    return (
      <div className="flex flex-column align-items-center justify-content-center p-4 gap-2">
        <i className="pi pi-info-circle text-2xl text-500" />
        <span className="text-sm text-600">Não foi possível carregar os detalhes do pedido de férias.</span>
        <Button label="Fechar" size="small" text onClick={onClose} />
      </div>
    );
  }

  return (
    <div className="flex flex-column h-full gap-3 p-1">
      {/* Card Principal com os Dados do Pedido */}
      <div
        className="border-round-xl p-3 flex flex-column gap-3"
        style={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        }}
      >
        {/* Cabeçalho do Card: Colaborador e Tipo */}
        <div className="flex align-items-center justify-content-between pb-2 border-bottom-1 surface-border">
          <div className="flex align-items-center gap-2">
            <div
              className="flex align-items-center justify-content-center border-round-xl"
              style={{
                width: "40px",
                height: "40px",
                backgroundColor: "#EFF6FF",
                color: "#0084D1",
              }}
            >
              <i className="pi pi-user text-lg" />
            </div>
            <div className="flex flex-column">
              <span className="font-bold text-sm text-900">
                {vacation.employee?.name ?? "Colaborador"}
              </span>
              <span className="text-xs text-500">
                {vacation.employee?.jobTitle?.name ?? "Colaborador"}
              </span>
            </div>
          </div>
          <span
            className="badge-squircle"
            style={{
              color: isCancellation ? "#DC2626" : "#D97706",
              backgroundColor: isCancellation ? "#FEF2F2" : "#FFFBEB",
              border: `1px solid ${isCancellation ? "#FECACA" : "#FDE68A"}`,
              fontSize: "0.75rem",
            }}
          >
            {isCancellation ? "Cancelamento" : "Pedido de Férias"}
          </span>
        </div>

        {/* Período Solicitado */}
        <div
          className="flex flex-column gap-2 p-3 border-round-lg"
          style={{ backgroundColor: "#F8FAFC" }}
        >
          <div className="flex align-items-center justify-content-between">
            <span className="text-xs text-600 font-medium">Período Solicitado:</span>
            <span className="text-xs font-bold text-900">
              {vacation.days ?? 0} {vacation.days === 1 ? "dia útil" : "dias úteis"}
            </span>
          </div>
          <div className="flex align-items-center gap-2 text-sm font-semibold text-800">
            <i className="pi pi-calendar" style={{ color: "#0084D1" }} />
            <span>
              {formatDate(vacation.startAt)} — {formatDate(vacation.endAt)}
            </span>
          </div>

          {/* Detalhe da distribuição dos dias (se aplicável) */}
          {(Boolean(vacation.daysLastYear) ||
            Boolean(vacation.daysCurrentYear) ||
            Boolean(vacation.daysNextYear)) && (
              <div className="flex flex-wrap gap-2 pt-2 border-top-1 surface-border text-xs text-600">
                {Boolean(vacation.daysLastYear) && (
                  <span>
                    Ano Anterior: <strong>{vacation.daysLastYear}d</strong>
                  </span>
                )}
                {Boolean(vacation.daysCurrentYear) && (
                  <span>
                    Ano Corrente: <strong>{vacation.daysCurrentYear}d</strong>
                  </span>
                )}
                {Boolean(vacation.daysNextYear) && (
                  <span>
                    Ano Seguinte: <strong>{vacation.daysNextYear}d</strong>
                  </span>
                )}
              </div>
            )}
          {Boolean(vacation.daysNextYear && vacation.daysNextYear > 0) && (
            <div
              className="w-full flex align-items-center gap-2 px-2 py-2 border-round-md mt-2"
              style={{ backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" }}
            >
              <i className="pi pi-exclamation-triangle text-sm" style={{ color: "#D97706" }} />
              <span className="text-xs font-medium" style={{ color: "#92400E" }}>
                Utiliza {vacation.daysNextYear} {vacation.daysNextYear === 1 ? "dia" : "dias"} de férias do  ano seguinte.
              </span>
            </div>
          )}
        </div>

        {/* Informações Complementares: Projetos e Data de Submissão */}
        <div className="flex flex-column gap-2 text-xs">
          {vacation.projects && vacation.projects.length > 0 && (
            <div className="flex align-items-center gap-2 text-700">
              <i className="pi pi-briefcase text-500" />
              <span>
                Projetos:{" "}
                <strong>
                  {vacation.projects
                    .map((p) => p.name)
                    .filter(Boolean)
                    .join(", ")}
                </strong>
              </span>
            </div>
          )}

          {vacation.createdAt && (
            <div className="flex align-items-center gap-2 text-500">
              <i className="pi pi-clock text-400" />
              <span>
                Submetido em: <strong>{formatDate(vacation.createdAt)}</strong>
              </span>
            </div>
          )}

          {vacation.rejectionReason && (
            <div className="p-2 border-round bg-red-50 text-red-700 border-1 border-red-200 mt-1">
              <span className="font-semibold block mb-1">Motivo anterior registado:</span>
              <span>{vacation.rejectionReason}</span>
            </div>
          )}
        </div>

        {/* Botões de Ação do Manager */}
        <div className="flex justify-content-end gap-2 pt-3 border-top-1 surface-border">
          <Button
            label={isCancellation ? "Recusar" : "Rejeitar"}
            icon="pi pi-times"
            severity="danger"
            outlined
            size="small"
            disabled={isSubmitting}
            onClick={() => setVisibleRejectionReason(true)}
          />
          <Button
            label={isCancellation ? "Aprovar Cancelamento" : "Aprovar"}
            icon="pi pi-check"
            size="small"
            disabled={isSubmitting}
            style={{ backgroundColor: "#059669", borderColor: "#059669" }}
            onClick={() =>
              handleApproveOrReject(
                isCancellation ? "Cancelado" : VacationStatusEnum.APROVADO
              )
            }
          />
        </div>
      </div>

      {/* Diálogo de Motivo da Rejeição */}
      <Dialog
        header={
          isCancellation
            ? "Recusa do Pedido de Cancelamento"
            : "Rejeição do Pedido de Férias"
        }
        visible={visibleRejectionReason}
        modal
        onHide={() => setVisibleRejectionReason(false)}
        className="mx-3"
        style={{ width: "92vw", maxWidth: "420px" }}
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="motivo" className="text-xs font-semibold text-700">
              Informe o Motivo da Rejeição:
            </label>
            <InputTextarea
              id="motivo"
              value={reasonReject}
              onChange={(e) => setReasonReject(e.target.value)}
              rows={4}
              autoFocus
              maxLength={190}
              className="mt-1"
            />
            <div className="flex justify-content-end mt-1">
              <small className="text-500">{reasonReject.length}/190 caracteres</small>
            </div>
          </div>
          <div className="flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              text
              size="small"
              onClick={() => setVisibleRejectionReason(false)}
            />
            <Button
              label="Confirmar"
              icon="pi pi-check"
              severity="danger"
              size="small"
              disabled={reasonReject.trim() === "" || isSubmitting}
              onClick={() => {
                if (reasonReject.trim() !== "") {
                  handleApproveOrReject(
                    isCancellation
                      ? VacationStatusEnum.APROVADO
                      : VacationStatusEnum.NAO_APROVADO
                  );
                }
              }}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};